/**
 * Beacon — external signal watcher (AMB-8).
 *
 * Zero-config competitive intelligence bound to the ratified plan: a derived
 * watchlist of external entities (competitors, technologies, regulations,
 * customers, markets) the plan already depends on, swept daily against
 * external sources, surfacing findings ONLY when a signal is material to a
 * currently ratified item or a plan assumption. Moments, not feeds.
 *
 * UNLIKE its five siblings, Beacon has no client-side detection path. Every
 * other intelligence agent computes over rows the browser can already read;
 * Beacon fetches EXTERNAL sources (Google News RSS in v1), and external
 * fetches must not run in the browser:
 *   - cross-origin fetches to news.google.com are blocked by CORS anyway;
 *   - the sweep budget (≤10 entities, ≤3 signals/entity, 1 metered LLM
 *     call/run) is enforced server-side and must not be replayable per tab;
 *   - beacon_signals is service-role write only (migration 596), so the
 *     dedupe-by-URL key cannot be maintained from the client.
 * So the batch runner asks the ambient edge function to run the agent now
 * (mode run_agent): the template runtime runs the same scan
 * (supabase/functions/ambient/lib/beaconScan.ts) it runs on Beacon's schedule,
 * and its announcement follows Beacon's delivery settings.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';

export const BEACON_AGENT_TYPE = 'beacon';

interface AmbientRunResponse {
  status?: string;
  stats?: Partial<BatchStats>;
  error?: string;
}

/**
 * Run Beacon once, server-side. scope/config are accepted for signature
 * parity with the other agents' runners (IntelligenceBatchRunner) but the
 * server run is always workspace-wide and reads its own agent_settings row.
 */
export async function runBeaconBatch(
  workspaceId: string,
  _scope: RunScope,
  _config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  if (signal.aborted) return stats;

  onProgress(0, 1);
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.functions.invoke('ambient', {
      // The template runtime runs the agent now, so its delivery settings decide who is told (migration 944).
      body: { mode: 'run_agent', workspace_id: workspaceId, agent_type: 'beacon' },
    });
    if (error) {
      stats.errors++;
      return stats;
    }
    const payload = (data ?? {}) as AmbientRunResponse;
    if (payload.error) {
      stats.errors++;
      return stats;
    }
    stats.processed = Number(payload.stats?.processed) || 0;
    stats.proposed = Number(payload.stats?.proposed) || 0;
    stats.skipped = Number(payload.stats?.skipped) || 0;
    stats.errors = Number(payload.stats?.errors) || 0;
    return stats;
  } catch {
    stats.errors++;
    return stats;
  } finally {
    onProgress(1, 1);
  }
}
